import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import '@/App.css';
import Layout from '@/components/Layout';
import Home from '@/pages/Home';
import About from '@/pages/About';
import Categories from '@/pages/Categories';
import Judging from '@/pages/Judging';
import Eligibility from '@/pages/Eligibility';
import Jury from '@/pages/Jury';
import Contact from '@/pages/Contact';
import EnterNow from '@/pages/EnterNow';
import Winners from '@/pages/Winners';
import Gallery from '@/pages/Gallery';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import Refund from '@/pages/Refund';
import JurorCode from '@/pages/JurorCode';
import PaymentTerms from '@/pages/PaymentTerms';
import IPPolicy from '@/pages/IPPolicy';
import Disclaimer from '@/pages/Disclaimer';
import CookiePolicy from '@/pages/CookiePolicy';
import EntrantConsent from '@/pages/EntrantConsent';
import PortalLayout from '@/components/PortalLayout';
import PortalDashboard from '@/pages/PortalDashboard';
import PortalEntries from '@/pages/PortalEntries';
import AdminLogin from '@/pages/AdminLogin';
import AdminDashboard from '@/pages/AdminDashboard';
import JuryLogin from '@/pages/JuryLogin';
import JuryDashboard from '@/pages/JuryDashboard';
import JuryEntries from '@/pages/JuryEntries';
import JuryEntryReview from '@/pages/JuryEntryReview';
import ProtectedRoute from '@/components/ProtectedRoute';
import JuryProtectedRoute from '@/components/JuryProtectedRoute';
import { AuthProvider } from '@/context/AuthContext';
import { JuryAuthProvider } from '@/context/JuryAuthContext';
import { NominatorAuthProvider } from '@/context/NominatorAuthContext';
import MetaPixelRouteTracker from '@/components/MetaPixelRouteTracker';
import { RecaptchaProvider } from '@/lib/recaptcha';
import NominatorLogin from '@/pages/NominatorLogin';

function App() {
  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <JuryAuthProvider>
            <NominatorAuthProvider>
            <Toaster position="top-right" theme="dark" toastOptions={{ style: { background: '#5A4733', color: '#fff', border: '1px solid rgba(201, 165, 108,0.4)', borderRadius: 0 } }} />
            <MetaPixelRouteTracker />
            <Routes>
              {/* Admin */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />

              {/* Jury Chamber */}
              <Route path="/jury-chamber/login" element={<JuryLogin />} />
              <Route path="/jury-chamber" element={<JuryProtectedRoute><JuryDashboard /></JuryProtectedRoute>} />
              <Route path="/jury-chamber/entries" element={<JuryProtectedRoute><JuryEntries /></JuryProtectedRoute>} />
              <Route path="/jury-chamber/entry/:id" element={<JuryProtectedRoute><JuryEntryReview /></JuryProtectedRoute>} />
              {/* Legacy /jury login redirects → chamber */}
              <Route path="/jury/login" element={<Navigate to="/jury-chamber/login" replace />} />
              <Route path="/jury/entries" element={<Navigate to="/jury-chamber/entries" replace />} />

              {/* Nominator portal login / register — needs reCAPTCHA for OTP dispatch. */}
              <Route path="/portal/login" element={<RecaptchaProvider><NominatorLogin /></RecaptchaProvider>} />
              <Route path="/portal/register" element={<RecaptchaProvider><NominatorLogin /></RecaptchaProvider>} />
              <Route path="/portal/entry/new" element={<Navigate to="/portal/new" replace />} />

              {/* Nomination Portal — gated by OTP verification. Wrapped in
                  RecaptchaProvider so any inline OTP-resend inside the portal
                  is protected the same way as the public register gate. */}
              <Route path="/portal" element={<RecaptchaProvider><PortalLayout /></RecaptchaProvider>}>
                <Route index element={<Navigate to="/portal/dashboard" replace />} />
                <Route path="dashboard" element={<PortalDashboard />} />
                <Route path="new" element={<EnterNow portalMode />} />
                <Route path="entries" element={<PortalEntries />} />
              </Route>

              {/* Public site — the ONLY branch that needs reCAPTCHA (Contact,
                  Sponsor, Subscribe, RegisterGate). Scoping the provider here
                  prevents the "invalid domain" badge from rendering on Admin
                  and Jury dashboards, which never call the widget. */}
              <Route path="/*" element={
                <RecaptchaProvider>
                  <Layout>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/categories" element={<Categories />} />
                    <Route path="/judging" element={<Judging />} />
                    <Route path="/jury" element={<Jury />} />
                    <Route path="/eligibility" element={<Eligibility />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/enter" element={<EnterNow />} />
                    <Route path="/winners" element={<Winners />} />
                    <Route path="/gallery" element={<Gallery />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/refund" element={<Refund />} />
                    <Route path="/juror-code-of-conduct" element={<JurorCode />} />
                    <Route path="/payment-terms" element={<PaymentTerms />} />
                    <Route path="/ip-policy" element={<IPPolicy />} />
                    <Route path="/disclaimer" element={<Disclaimer />} />
                    <Route path="/cookie-policy" element={<CookiePolicy />} />
                    <Route path="/entrant-consent" element={<EntrantConsent />} />
                    <Route path="*" element={<Home />} />
                  </Routes>
                </Layout>
                </RecaptchaProvider>
              } />
            </Routes>
            </NominatorAuthProvider>
          </JuryAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;
